import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import {
  FaUsers,
  FaClipboardList,
  FaCalendarCheck,
  FaCheckCircle,
  FaRupeeSign,
  FaArrowUp,
  FaArrowDown,
  FaExchangeAlt,
  FaUserTie
} from 'react-icons/fa';
import { dsoApi } from '../../services/api';
import { Spinner, Button, ButtonGroup, Row, Col, Card } from 'react-bootstrap';


function DSOHome() {
  const navigate = useNavigate();
  const { getCurrentUser } = useAuth();
  const currentUser = getCurrentUser();

  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('daily');

  useEffect(() => {
    const checkProfileStatus = async () => {
      try {
        const response = await dsoApi.getDsoProfile();
        const dsoProfile = response.data.dso_profile;

        // Check if all required steps are completed
        const isBusinessProfile = dsoProfile.applicant_type === 'Business';

        // Check for agreement and verification
        if (!dsoProfile.agreement_signed || !dsoProfile.is_verified) {
          // If agreement is not signed or profile is not verified, redirect to onboarding
          navigate('/dso/onboarding', { replace: true });
          return;
        }

        // Check for required documents based on profile type
        if (isBusinessProfile) {
          if (!dsoProfile.company_pan_card_file || !dsoProfile.company_certificate_file) {
            navigate('/dso/onboarding', { replace: true });
            return;
          }
        } else {
          if (!dsoProfile.adhaar_card_front_file || !dsoProfile.adhaar_card_back_file || !dsoProfile.pan_card_file) {
            navigate('/dso/onboarding', { replace: true });
            return;
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching DSO profile:", err);
        setLoading(false);
      }
    };

    checkProfileStatus();
  }, [navigate]);

  // State for API data
  const [dashboardData, setDashboardData] = useState({
    dashboardStats: null
  });

  // Fetch dashboard data when period changes
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Use BatchTool pattern to fetch multiple API endpoints in parallel
        const dashboardStatsResponse = await dsoApi.getEarningsSummary(); // Gets summary stats for the dashboard

        setDashboardData({
          dashboardStats: dashboardStatsResponse.data
        });
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        // Set empty data on error
        setDashboardData({
          dashboardStats: null
        });
      }
    };

    fetchDashboardData();
  }, [selectedPeriod]);

  // Get dashboard stats from API data
  const getStatsForPeriod = (period) => {
    const stats = dashboardData.dashboardStats;

    if (stats) {
      return [
        {
          id: 1,
          title: 'Total Leads',
          value: stats.total_leads ? stats.total_leads.toString() : '0',
          icon: <FaClipboardList />,
          color: '#4c51bf',
          actionLabel: 'Add Lead',
          actionRoute: '/dso/leads'
        },
        {
          id: 2,
          title: 'Conversion Rate',
          value: stats.conversion_rate ? `${stats.conversion_rate}%` : '0%',
          icon: <FaExchangeAlt />,
          color: '#38b2ac',
          actionLabel: 'Improve'
        },
        {
          id: 3,
          title: 'Active Agents',
          value: stats.active_agents ? stats.active_agents.toString() : '0',
          icon: <FaUserTie />,
          color: '#ed8936',
          actionLabel: 'Add Agent',
          actionRoute: '/dso/team'
        }
      ];
    }

    // Fallback to default empty stats if no API data is available
    return [
      { id: 1, title: 'Total Leads', value: '0', icon: <FaClipboardList />, color: '#4c51bf', actionLabel: 'Add Lead', actionRoute: '/dso/leads' },
      { id: 2, title: 'Conversion Rate', value: '0%', icon: <FaExchangeAlt />, color: '#38b2ac', actionLabel: 'Improve' },
      { id: 3, title: 'Active Agents', value: '0', icon: <FaUserTie />, color: '#ed8936', actionLabel: 'Add Agent', actionRoute: '/dso/team' }
    ];
  };

  // State for recent leads and top products - should come from API
  const [recentLeads, setRecentLeads] = useState([]);
  const [topProducts, setTopProducts] = useState([]);

  // Fetch recent leads and top products when dashboard loads
  useEffect(() => {
    const fetchRecentData = async () => {
      try {
        // Get recent leads (last 5)
        const leadsResponse = await dsoApi.getLeads({ limit: 5 });
        if (leadsResponse.data && leadsResponse.data.items) {
          const formattedLeads = leadsResponse.data.items.map(lead => ({
            id: lead.id,
            name: lead.name || 'Unknown',
            product: lead.product || 'Unknown Product',
            status: lead.case_status_str ? lead.case_status_str.toLowerCase() : 'new',
            date: lead.created_at ? new Date(lead.created_at).toISOString().split('T')[0] : 'N/A'
          }));
          setRecentLeads(formattedLeads);
        }

        // Get top products based on leads count
        const productsStatsResponse = await dsoApi.getEarningsByCategory();
        if (productsStatsResponse.data) {
          const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042'];
          const formattedProducts = productsStatsResponse.data.map((product, index) => ({
            id: index + 1,
            name: product.category_name || 'Other',
            count: product.leads_count || 0,
            amount: `₹${(product.total_amount || 0).toLocaleString()}`,
            color: colors[index % colors.length]
          }));
          setTopProducts(formattedProducts);
        }
      } catch (error) {
        console.error("Error fetching recent data:", error);
        // Set empty arrays on error
        setRecentLeads([]);
        setTopProducts([]);
      }
    };

    fetchRecentData();
  }, []);

  // This mock data is no longer used - API data is used instead

  const stats = getStatsForPeriod(selectedPeriod);
  // Use real API data only - no more mock data


  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
        <p className="mt-3">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <>
      <div className="dso-home-dashboard">
      <div className="dso-dashboard-header">
        <div className="dso-dashboard-header-content">
          <h1 className="dso-welcome-title">Welcome, {currentUser?.name || 'Direct Sales Officer'}</h1>

          <div className="dso-period-filter">
            <ButtonGroup size="sm" className="dso-pill-group">
              <Button
                variant={selectedPeriod === 'daily' ? 'primary' : 'outline-primary'}
                onClick={() => setSelectedPeriod('daily')}
              >
                Daily
              </Button>
              <Button
                variant={selectedPeriod === 'weekly' ? 'primary' : 'outline-primary'}
                onClick={() => setSelectedPeriod('weekly')}
              >
                Weekly
              </Button>
              <Button
                variant={selectedPeriod === 'monthly' ? 'primary' : 'outline-primary'}
                onClick={() => setSelectedPeriod('monthly')}
              >
                Monthly
              </Button>
              <Button
                variant={selectedPeriod === 'lastMonth' ? 'primary' : 'outline-primary'}
                onClick={() => setSelectedPeriod('lastMonth')}
              >
                Last Month
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </div>

      <div className="dso-stats-overview">
        <Row className="g-2">
          {stats.map(stat => (
            <Col lg={3} md={6} sm={6} key={stat.id} className="mb-2">
              <Card className="dso-stat-card h-100">
                <Card.Body className="compact-card">
                  <div className="stat-card-content">
                    <div className="stat-icon-container" style={{ backgroundColor: `${stat.color}15` }}>
                      <div className="stat-icon" style={{ color: stat.color }}>
                        {stat.icon}
                      </div>
                    </div>
                    <div className="stat-details">
                      <div className="stat-title">{stat.title}</div>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="stat-value">{stat.value}</div>
                        {stat.value === '0' || stat.value === '₹0' || stat.value === '0%' ? (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="action-pill-button"
                            onClick={() => navigate(stat.actionRoute || '/dso/leads')}
                          >
                            {stat.actionLabel || 'Add'}
                          </Button>
                        ) : (
                          <div className={`stat-change positive`}>
                            <FaArrowUp className="change-icon" />
                            <span className="change-percent">({stat.changePercent})</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>


      {/* Data Section */}
      <div className="dso-dashboard-row mt-2">
        <div className="dso-dashboard-card">
          <div className="dso-card-header">
            <h3>Recent Leads</h3>
            <button
              className="dso-view-all-btn"
              onClick={() => navigate('/dso/leads')}
            >
              View All
            </button>
          </div>
          <div className="dso-card-body compact-body">
            <div className="table-responsive">
              <table className="dso-table compact-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Product</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLeads && recentLeads.length > 0 ? (
                    recentLeads.map(lead => (
                      <tr key={lead.id}>
                        <td>{lead.name}</td>
                        <td>{lead.product}</td>
                        <td>
                          <span className={`dso-status-badge ${lead.status ? lead.status.toLowerCase().replace(/\s+/g, '-') : 'new'}`}>
                            {lead.status ? lead.status : 'New'}
                          </span>
                        </td>
                        <td>{lead.date}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-3">
                        <div className="text-muted">No data found</div>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="mt-2"
                          onClick={() => navigate('/dso/leads')}
                        >
                          Add Lead
                        </Button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="dso-dashboard-card">
          <div className="dso-card-header">
            <h3>Top Products</h3>
          </div>
          <div className="dso-card-body compact-body">
            {topProducts && topProducts.length > 0 ? (
              topProducts.map((product, index) => (
                <div className="dso-product-item compact-item" key={product.id}>
                  <div className="dso-product-rank">{index + 1}</div>
                  <div className="dso-product-bar-container">
                    <div className="dso-product-details">
                      <div className="dso-product-name">
                        {product.name}
                        <span className="ms-2 product-amount">{product.amount}</span>
                      </div>
                    </div>
                    <div className="dso-product-bar-wrapper">
                      <div
                        className="dso-product-bar"
                        style={{
                          width: `${(product.count / topProducts[0].count) * 100}%`,
                          backgroundColor: product.color
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <p className="text-muted mb-2">No data found</p>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => navigate('/dso/products')}
                >
                  View Products
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CSS styles */}
      <style>{`
        .dso-dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding: 8px 0;
          border-bottom: 1px solid #f1f5f9;
        }

        .dso-dashboard-header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          flex-wrap: wrap;
          gap: 10px;
        }

        .dso-welcome-title {
          font-size: 1.2rem;
          font-weight: 600;
          margin: 0;
          color: #1e293b;
        }

        .dso-period-filter {
          margin: 0;
        }

        .dso-stats-overview {
          margin-bottom: 16px;
        }

        .dso-stat-card {
          border: none;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          transition: transform 0.2s;
          overflow: hidden;
        }

        .dso-stat-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
        }

        .compact-card {
          padding: 12px !important;
        }

        .stat-card-content {
          display: flex;
          align-items: center;
        }

        .stat-icon-container {
          width: 48px;
          height: 48px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          flex-shrink: 0;
        }

        .stat-icon {
          font-size: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .action-pill-button {
          border-radius: 20px;
          font-size: 0.7rem;
          padding: 2px 8px;
          white-space: nowrap;
        }

        .stat-details {
          flex: 1;
          min-width: 0;
        }

        .stat-title {
          color: #64748b;
          font-size: 0.75rem;
          margin-bottom: 4px;
          white-space: nowrap;
        }

        .stat-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0;
        }

        .stat-change {
          display: flex;
          align-items: center;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .stat-change.positive {
          color: #10b981;
        }

        .stat-change.negative {
          color: #ef4444;
        }

        .change-icon {
          font-size: 10px;
          margin-right: 2px;
        }

        .change-value {
          margin-right: 2px;
        }

        .change-percent {
          font-size: 0.7rem;
          opacity: 0.8;
        }

        .dso-dashboard-row {
          display: grid;
          grid-template-columns: 3fr 2fr;
          gap: 16px;
        }

        .dso-dashboard-card {
          background-color: #fff;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }

        .dso-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 16px;
          border-bottom: 1px solid #f1f5f9;
        }

        .dso-card-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
        }

        .dso-card-body {
          padding: 16px;
        }

        .compact-body {
          padding: 12px;
        }

        .dso-view-all-btn {
          background: none;
          border: none;
          color: #6366f1;
          font-size: 0.75rem;
          cursor: pointer;
          padding: 0;
          font-weight: 500;
        }

        .dso-view-all-btn:hover {
          text-decoration: underline;
        }

        .table-responsive {
          overflow-x: auto;
          margin: -8px -12px;
          padding: 8px 12px;
        }

        .dso-table {
          width: 100%;
          border-collapse: collapse;
        }

        .compact-table th {
          padding: 8px 10px;
          text-align: left;
          font-size: 0.7rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 1px solid #f1f5f9;
        }

        .compact-table td {
          padding: 8px 10px;
          font-size: 0.8rem;
          color: #334155;
          border-bottom: 1px solid #f1f5f9;
        }

        .dso-table tr:last-child td {
          border-bottom: none;
        }

        .dso-status-badge {
          display: inline-block;
          padding: 2px 6px;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 500;
        }

        .dso-status-badge.new {
          background-color: #e0f2fe;
          color: #0369a1;
        }

        .dso-status-badge.contacted {
          background-color: #fef9c3;
          color: #854d0e;
        }

        .dso-status-badge.interested {
          background-color: #dcfce7;
          color: #15803d;
        }

        .dso-status-badge.closed {
          background-color: #dbeafe;
          color: #1e40af;
        }

        .dso-status-badge.rejected {
          background-color: #fee2e2;
          color: #b91c1c;
        }

        /* Top Products Styling */
        .dso-product-item {
          display: flex;
          align-items: center;
          margin-bottom: 10px;
        }

        .compact-item {
          margin-bottom: 8px;
        }

        .dso-product-item:last-child {
          margin-bottom: 0;
        }

        .dso-product-rank {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background-color: #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          color: #64748b;
          font-size: 0.75rem;
          margin-right: 10px;
          flex-shrink: 0;
        }

        .dso-product-bar-container {
          flex: 1;
        }

        .dso-product-details {
          margin-bottom: 4px;
        }

        .dso-product-name {
          font-weight: 500;
          font-size: 0.8rem;
          color: #334155;
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
        }

        .product-amount {
          font-weight: 600;
          font-size: 0.75rem;
          color: #334155;
        }

        .dso-product-bar-wrapper {
          height: 6px;
          background-color: #f1f5f9;
          border-radius: 3px;
          overflow: hidden;
        }

        .dso-product-bar {
          height: 100%;
          border-radius: 3px;
          transition: width 0.8s ease-in-out;
        }

        /* Responsive Adjustments */
        @media (max-width: 992px) {
          .dso-dashboard-row {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .dso-dashboard-header-content {
            flex-direction: column;
            align-items: flex-start;
          }

          .dso-period-filter {
            width: 100%;
            margin-top: 8px;
            overflow-x: auto;
          }
        }
      `}
      </style>
      </div>
    </>
  );
}

export default DSOHome;